import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import { disabledGray, primaryBlue } from '../../../constants/colors';
import DocumentUploader from '../../DocumentUploader';

export interface IUploadProps {
  open: boolean;
  onClose?: () => void;
  isSending?: boolean;
  handleFileUploader: (value: File[]) => void;
  orderDocs: any;
  uploadedFile: File | null;
  setUploadedFile: (value: File | null) => void;
  fileName: string;
  setFileName: (value: string) => void;
  error: string | null;
  setError: (value: string | null) => void;
}

const UploadInkSignedDocument = (props: IUploadProps) => {
  const {
    onClose,
    open,
    isSending,
    handleFileUploader,
    orderDocs,
    error,
    fileName,
    setError,
    setFileName,
    setUploadedFile,
    uploadedFile,
  } = props;

  const handleClickClose = () => {
    if (onClose) {
      setFileName('');
      setUploadedFile(null);
      onClose();
    }
  };

  const checkNameDuplicate = (name: any) => {
    const duplicatedNames = orderDocs?.filter((x: Record<string, string>) =>
      x.Name.includes(name)
    );
    return duplicatedNames.length > 0;
  };

  const checkFileSize = (file: any) => {
    let isOversize = false;
    // If larger than 10mb
    if (file.size >= 10485760) {
      isOversize = true;
    }
    return isOversize;
  };

  const validateFileToUpload = (file: any) => {
    if (checkNameDuplicate(file.name)) {
      setError(
        'Another file already exists with this name, please rename your file'
      );
    } else if (checkFileSize(file)) {
      setError('Your file size is larger than 10mb');
    }
  };

  const handleDocumentUploadLocal = (file: any) => {
    validateFileToUpload(file[0]);
    setUploadedFile(file[0]);
  };

  const handleUploadFile = () => {
    if (uploadedFile) {
      if (fileName.length > 0) {
        const fileToUpload = new File([uploadedFile], fileName);
        handleFileUploader([fileToUpload]);
      } else {
        handleFileUploader([uploadedFile]);
      }
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='sm'
        open={open as boolean}
        scroll={'paper'}
        aria-labelledby='form-dialog-title'>
        <DialogTitle id='form-dialog-title'>
          Please upload ink sign document
        </DialogTitle>
        <>
          {isSending ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '200px',
              }}>
              <Typography variant='h6'>Uploading Document...</Typography>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DialogContent dividers>
                {uploadedFile ? (
                  <>
                    <Typography align='center' gutterBottom color='red'>
                      {error}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: disabledGray,
                        borderRadius: 1,
                        color: primaryBlue,
                        paddingLeft: 2,
                        paddingRight: 1,
                        marginBottom: 2,
                        flexDirection: 'row',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <div>
                        {fileName ? fileName + '.pdf' : uploadedFile.name}
                      </div>
                      <IconButton
                        onClick={() => {
                          setFileName('');
                          setUploadedFile(null);
                          setError(null);
                        }}
                        color='primary'>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <TextField
                      id='standard-basic'
                      label='Rename Uploaded File'
                      variant='standard'
                      size='small'
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <EditIcon />
                          </InputAdornment>
                        ),
                      }}
                      onChange={(v) => {
                        setFileName(v.target.value);
                        if (!checkNameDuplicate(v.target.value)) {
                          setError(null);
                        }
                      }}
                    />
                  </>
                ) : (
                  <Box sx={{ marginTop: '20px' }}>
                    <DocumentUploader
                      data-testid='uploadField'
                      text='Click or Drag and Drop File here'
                      multiple={false}
                      maxSize={70}
                      docType='.pdf'
                      startUploadFiles={handleDocumentUploadLocal}
                    />
                    <Typography variant='caption' gutterBottom>
                      Max file size: 10mb
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button onClick={handleClickClose} variant='text'>
                  Cancel
                </Button>
                <Button
                  role='button'
                  data-testid='invite-btn'
                  variant='contained'
                  color='primary'
                  disabled={error != null}
                  onClick={handleUploadFile}>
                  Upload
                </Button>
              </DialogActions>
            </>
          )}
        </>
      </Dialog>
    </>
  );
};

export default UploadInkSignedDocument;
